import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import { useSaveContact } from './hooks/useSaveContact';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { ContactFormParams } from 'assets/js/pages/contacts/contactFormParams';
import { useContactFormSections } from 'assets/js/pages/contacts/hooks/useContactFormSections';
import { useDefaultContactFormValues } from 'assets/js/pages/contacts/hooks/useDefaultContactFormValues';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { ContactType } from 'constants/contacts';
import { useQueryParams } from 'customHooks/useQueryParams';

const EditContactFormDrawer = () => {
    const { goBack } = useDialogRoute();
    const queryParams = useQueryParams<ContactFormParams>();
    const sections = useContactFormSections();

    const [isAddingContactFile, setIsAddingContactFile] = useState(false);
    const autoFocusField = queryParams.autoFocus;

    const defaultValues = useDefaultContactFormValues();

    const methods = useGivebutterForm<ContactFormValues>({
        defaultValues,
    });

    const { handleSubmit, setFocus, watch } = methods;

    const contactType = watch('type');

    const { mutate: submit, isPending } = useSaveContact(methods, {
        onSuccess: () => {
            goBack();
        },
    });

    const getDrawerTitle = () => {
        if (sections.social) return 'Edit social accounts';
        if (sections.emails) return 'Edit emails';
        if (sections.phones) return 'Edit phones';
        if (sections.engage) return 'Edit subscriptions';
        if (sections.customFields) return 'Edit Custom Fields';

        return 'Edit profile information';
    };

    useEffect(() => {
        if (autoFocusField) {
            setTimeout(() => setFocus(autoFocusField), 0);
        }
    }, [autoFocusField, setFocus]);

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>{getDrawerTitle()}</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <ContactForm.Container>
                        {contactType === ContactType.Company && sections.profile && (
                            <CompanyForm.Name />
                        )}
                        {sections.profile ? (
                            <>
                                {contactType === ContactType.Individual && (
                                    <>
                                        <ContactForm.FirstName required={false} />
                                        <ContactForm.MiddleName />
                                        <ContactForm.LastName required={false} />
                                        <ContactForm.Dob />
                                        <ContactForm.Gender />
                                        <ContactForm.Employer />
                                        <ContactForm.Title />
                                    </>
                                )}
                                <ContactForm.Separator />
                                <ContactForm.Notes />
                            </>
                        ) : (
                            <>
                                {sections.social && <ContactForm.Socials />}
                                {sections.emails && (
                                    <ContactForm.Emails
                                        name={
                                            contactType === ContactType.Company
                                                ? 'companyEmails'
                                                : 'emails'
                                        }
                                        showActions
                                    />
                                )}
                                {sections.phones && (
                                    <ContactForm.Phones
                                        name={
                                            contactType === ContactType.Company
                                                ? 'companyPhones'
                                                : 'phones'
                                        }
                                        showActions
                                    />
                                )}
                                {sections.customFields && (
                                    <ContactForm.CustomFields
                                        onAddContactFile={setIsAddingContactFile}
                                        type={contactType}
                                    />
                                )}
                                {sections.engage && <ContactForm.Engage />}
                            </>
                        )}
                    </ContactForm.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'transparent'}>Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton isSubmitting={isAddingContactFile || isPending}>
                                Update
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const EditContactFormDrawerTrigger = Drawer.Trigger;

const Root = EditContactFormDrawer;
const Trigger = EditContactFormDrawerTrigger;

export {
    EditContactFormDrawer,
    EditContactFormDrawerTrigger,
    //
    Root,
    Trigger,
};
